import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import globalFunction from "../../utils/globalFunction";
import axios from "axios";
import { API_END_POINTS } from "../ApiEndPoints";

const initialState = {
  isLoading: false,
  isError: null,
  message: null,
  conversationHistory: null,
  growthCoordinatorHistory: null,
  certifyMeHistory: null,
  certificationReadyCourses: null,
};

const handleUnauthorizedError = (message) => {
  if (
    message === "Unauthorized" ||
    message?.toLowerCase().includes("user not authorized") ||
    message?.toLowerCase().includes("token has expired") ||
    message?.toLowerCase().includes("jwt expire")
  ) {
    globalFunction.logoutFunction();
  }
};

export const studentAssessMeAPI = createAsyncThunk(
  "studentAI/studentAssessMeAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.studentAssessMe,
        { input: credentials.input },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch AssessMe response"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

export const getAssessMeHistoryAPI = createAsyncThunk(
  "studentAI/getAssessMeHistoryAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const { token, page = 1, limit = 20 } = credentials;
      const response = await axios.get(
        `${API_END_POINTS.studentAssessMe}/history?page=${page}&limit=${limit}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch AssessMe history"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

export const getGrowthCoordinatorHistoryAPI = createAsyncThunk(
  "studentAI/getGrowthCoordinatorHistoryAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const { token, page = 1, limit = 20 } = credentials;
      const response = await axios.get(
        `${API_END_POINTS.studentGrowthCoordinator}/history?page=${page}&limit=${limit}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch Growth Coordinator history"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

export const studentGrowthCoordinatorAPI = createAsyncThunk(
  "studentAI/studentGrowthCoordinatorAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.studentGrowthCoordinator,
        { input: credentials.input },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch Growth Coordinator response"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

// ==================== CERTIFYME.AI ASYNC THUNKS ====================

export const getCertificationReadyCoursesAPI = createAsyncThunk(
  "studentAI/getCertificationReadyCoursesAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const { token } = credentials;
      const response = await axios.get(
        API_END_POINTS.studentCertifyMe + "/courses",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch certification ready courses"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

export const studentCertifyMeAPI = createAsyncThunk(
  "studentAI/studentCertifyMeAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const { token, input, courseId, sessionId } = credentials;
      const response = await axios.post(
        API_END_POINTS.studentCertifyMe + "/chat",
        { input, courseId, sessionId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch CertifyMe response"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

export const getCertifyMeHistoryAPI = createAsyncThunk(
  "studentAI/getCertifyMeHistoryAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const { token, courseId, page = 1, limit = 20 } = credentials;
      const response = await axios.get(
        `${API_END_POINTS.studentCertifyMe}/history?courseId=${courseId || ''}&page=${page}&limit=${limit}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to fetch CertifyMe history"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

export const checkCertificationPassAPI = createAsyncThunk(
  "studentAI/checkCertificationPassAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const { token, courseId, sessionId } = credentials;
      const response = await axios.get(
        `${API_END_POINTS.studentCertifyMe}/check-pass/${courseId}/${sessionId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to check certification pass status"
        );
      } else {
        return rejectWithValue("Something went wrong! Please try again later.");
      }
    }
  }
);

const studentAiSlice = createSlice({
  name: "studentAI",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(studentAssessMeAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(studentAssessMeAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "AssessMe response fetched successfully";
      })
      .addCase(studentAssessMeAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload || "Failed to fetch AssessMe response";
        state.message = null;
      })
      .addCase(getAssessMeHistoryAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(getAssessMeHistoryAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "AssessMe history fetched successfully";
        state.conversationHistory = action.payload.data;
      })
        .addCase(getAssessMeHistoryAPI.rejected, (state, action) => {
          state.isLoading = false;
          state.isError = action.payload || "Failed to fetch AssessMe history";
          state.message = null;
        })
        .addCase(getGrowthCoordinatorHistoryAPI.pending, (state) => {
          state.isLoading = true;
          state.isError = null;
          state.message = null;
        })
        .addCase(getGrowthCoordinatorHistoryAPI.fulfilled, (state, action) => {
          state.isLoading = false;
          state.isError = null;
          state.message = action.payload.message || "Growth Coordinator history fetched successfully";
          state.growthCoordinatorHistory = action.payload.data;
        })
        .addCase(getGrowthCoordinatorHistoryAPI.rejected, (state, action) => {
          state.isLoading = false;
          state.isError = action.payload || "Failed to fetch Growth Coordinator history";
          state.message = null;
        })
      .addCase(studentGrowthCoordinatorAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(studentGrowthCoordinatorAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "Growth Coordinator response fetched successfully";
      })
      .addCase(studentGrowthCoordinatorAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload || "Failed to fetch Growth Coordinator response";
        state.message = null;
      })
      // CertifyMe.AI reducers
      .addCase(getCertificationReadyCoursesAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(getCertificationReadyCoursesAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "Certification ready courses fetched successfully";
        state.certificationReadyCourses = action.payload.data;
      })
      .addCase(getCertificationReadyCoursesAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload || "Failed to fetch certification ready courses";
        state.message = null;
      })
      .addCase(studentCertifyMeAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(studentCertifyMeAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "CertifyMe response fetched successfully";
      })
      .addCase(studentCertifyMeAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload || "Failed to fetch CertifyMe response";
        state.message = null;
      })
      .addCase(getCertifyMeHistoryAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(getCertifyMeHistoryAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "CertifyMe history fetched successfully";
        state.certifyMeHistory = action.payload.data;
      })
      .addCase(getCertifyMeHistoryAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload || "Failed to fetch CertifyMe history";
        state.message = null;
      })
      .addCase(checkCertificationPassAPI.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.message = null;
      })
      .addCase(checkCertificationPassAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = null;
        state.message = action.payload.message || "Certification pass status checked successfully";
      })
      .addCase(checkCertificationPassAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload || "Failed to check certification pass status";
        state.message = null;
      });
  },
});

export default studentAiSlice.reducer;